import { useCallback, useRef, useState } from "react";
import { ApiError } from "@/lib/api-client/authenticatedClient";
import { toast } from "@/lib/toast";

interface RequestWrapperState {
  isLoading: boolean;
  error: Error | null;
}

export function useRequestWrapper<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
): [(...args: TArgs) => Promise<TResult>, RequestWrapperState] {
  const fnRef = useRef(fn);
  // eslint-disable-next-line react-hooks/refs -- keep wrapped callback stable without fn in deps
  fnRef.current = fn;

  const [state, setState] = useState<RequestWrapperState>({
    isLoading: false,
    error: null,
  });

  const wrapped = useCallback(async (...args: TArgs): Promise<TResult> => {
    setState({ isLoading: true, error: null });
    try {
      const result = await fnRef.current(...args);
      setState({ isLoading: false, error: null });
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setState({ isLoading: false, error });
      toast.error(extractMessage(err));
      throw err;
    }
  }, []);

  return [wrapped, state];
}

const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again.";

function extractMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const fromBody = formatApiErrorBody(err.body);
    if (fromBody) return fromBody;
    return err.message || DEFAULT_ERROR_MESSAGE;
  }
  if (err instanceof Error) {
    return err.message || DEFAULT_ERROR_MESSAGE;
  }
  return DEFAULT_ERROR_MESSAGE;
}

function formatApiErrorBody(body: unknown): string | undefined {
  if (
    !body ||
    typeof body !== "object" ||
    !("title" in body) ||
    typeof body.title !== "string"
  ) {
    return undefined;
  }

  const errors =
    "errors" in body && body.errors && typeof body.errors === "object"
      ? (body.errors as Record<string, unknown>)
      : undefined;

  if (errors) {
    const pathError = getFirstErrorMessage(
      Object.entries(errors).filter(([key]) => key.startsWith("$")),
    );
    if (pathError) return pathError;

    const fieldError = getFirstErrorMessage(
      Object.entries(errors).filter(([key]) => key !== "request"),
    );
    if (fieldError) return fieldError;

    const anyError = getFirstErrorMessage(Object.entries(errors));
    if (anyError) return anyError;
  }

  return body.title;
}

function getFirstErrorMessage(
  entries: [string, unknown][],
): string | undefined {
  for (const [, value] of entries) {
    if (Array.isArray(value) && typeof value[0] === "string") {
      return value[0];
    }
  }
  return undefined;
}
